import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getMyNotificationsQuery,
  updateNotificationsQuery,
  deleteNotificationsQuery,
} from "./queries";
import { QUERY_STATUS } from "constants/constants";
import _ from "lodash";

const AlertsContext = createContext({});

export const useAlertsContext = () => useContext(AlertsContext);

export const AlertsProvider = ({ ...props }) => {
  const [notifications, setNotifications] = useState({});
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState({});
  const [loadNotificationsStatus, setLoadNotificationsStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const getNotifications = useCallback(
    async ({ lang, page, limit, archived, read }) => {
      setLoadNotificationsStatus(QUERY_STATUS.LOADING);
      try {
        const result = await getMyNotificationsQuery({
          lang,
          page,
          limit,
          archived,
          read,
        });

        setNotifications((old) => {
          const isFirstPage = _.isEmpty(old.content);
          const newContent = isFirstPage
            ? result?.content
            : [...(old?.content || []), ...(result?.content || [])];

          return {
            ...old,
            content: newContent,
            page: isFirstPage ? 1 : page,
            total_items: isFirstPage ? result?.total_items : old.total_items,
            total_pages: isFirstPage ? result?.total_pages : old.total_pages,
            items_per_page: isFirstPage
              ? result?.items_per_page
              : old.items_per_page,
          };
        });
        setLoadNotificationsStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setLoadNotificationsStatus(QUERY_STATUS.ERROR);
        console.error("getNotifications", error);
      }
    },
    []
  );

  const getUnreadNotifications = async () => {
    const unreadArchivedCount = await getMyNotificationsQuery({
      archived: "true",
      read: "false",
    });
    const unreadInboxCount = await getMyNotificationsQuery({
      archived: "false",
      read: "false",
    });
    setUnreadNotificationsCount({
      inbox: unreadInboxCount?.total_items,
      archived: unreadArchivedCount?.total_items,
    });
  };

  const updateNotifications = useCallback(async ({ data }) => {
    try {
      await updateNotificationsQuery({ data });
      getUnreadNotifications();
    } catch (e) {
      console.error("updateNotifications", e);
      throw e;
    }
  }, []);

  const deleteNotifications = useCallback(async ({ data }) => {
    try {
      await deleteNotificationsQuery({ data });
      getUnreadNotifications();
    } catch (e) {
      console.error("deleteNotifications", e);
      throw e;
    }
  }, []);

  const values = useMemo(
    () => ({
      notifications,
      getNotifications,
      updateNotifications,
      deleteNotifications,
      setNotifications,
      loadNotificationsStatus,
      unreadNotificationsCount,
      getUnreadNotifications,
    }),
    [
      notifications,
      unreadNotificationsCount,
      getNotifications,
      updateNotifications,
      deleteNotifications,
      setNotifications,
      loadNotificationsStatus,
    ]
  );

  return <AlertsContext.Provider value={values} {...props} />;
};
